import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {IResourceTypeItem} from '@toolify/client/src/modules/resources/types/IResourceTypeItem'
import {IResponseResource} from '@toolify/server/src/services/ResourceService/types/IResponseResource'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {IDatabaseViewResponseInstance} from '@toolify/server/src/controllers/DatabaseViewController/types/IDatabaseViewResponseInstance'
import {ICloudItemResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/ICloudItemResponseInstance'
import {CloudItemType} from '@toolify/server/src/models/mongoose/CloudItemModel/enum/CloudItemType'
import {IDatabasePropertyResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import {IDatabaseSliceResponseInstance} from '@toolify/server/src/controllers/DatabaseSliceController/types/IDatabaseSliceResponseInstance'
import {IConnectionDefinitionResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IConnectionDefinitionResponseInstance'
import {IDynamicFieldDefinitionResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDynamicFieldDefinitionResponseInstance'
import {IWebhookListenerDefinitionResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IWebhookListenerDefinitionResponseInstance'
import {IResourceItemResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IResourceItemResponseInstance'
import {IDatabaseItemResponseInstance} from '@toolify/server/src/controllers/DatabaseItemController/types/IDatabaseItemResponseInstance'
import {IDashboardItemResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDashboardItemResponseInstance'
import {IAutomationAppItemResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IAutomationAppItemResponseInstance'
import {IAutomationVersionResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IAutomationVersionResponseInstance'
import {IAutomationRunResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IAutomationRunResponseInstance'

export const ResourceTypeDataMap = new Map<ResourceType,(resource: IResponseResource) => IResourceTypeItem>([
  [ResourceType.Workspace, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.WorkspaceHome,
        params: {
          workspaceName: (resource.payload as {name?: string})?.name,
        },
      },
      iconClass: IconNameType.House,
    }
  }],
  [ResourceType.Database, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.Database,
        params: {
          databaseId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Database,
    }
  }],
  [ResourceType.DatabaseView, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IDatabaseViewResponseInstance
    return {
      link: {
        name: RouteName.DatabaseView,
        params: {
          databaseId: payload.databaseId,
          databaseViewId: resource.resourceId,
        },
      },
      // Fallback only; favourites/sidebar typically use `resourceItem.iconNameType`
      iconClass: IconNameType.Table,
    }
  }],
  [ResourceType.Conversation, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.Conversation,
        params: {
          conversationId: resource.resourceId,
        },
      },
      iconClass: IconNameType.CommentLines,
    }
  }],
  [ResourceType.DirectConversation, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.DirectConversation,
        params: {
          directConversationId: resource.resourceId,
        },
      },
      iconClass: IconNameType.CommentLines,
    }
  }],
  [ResourceType.CallRoom, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.CallRoom,
        params: {
          callRoomId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Microphone,
    }
  }],
  [ResourceType.Dashboard, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.Dashboard,
        params: {
          dashboardId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Grid2,
    }
  }],
  [ResourceType.Automation, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.Automation,
        params: {
          automationId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Bolt,
    }
  }],
  [ResourceType.Folder, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.Folder,
        params: {
          folderId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Files,
    }
  }],
  [ResourceType.CloudItem, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as ICloudItemResponseInstance
    // Payload can be null when the map is called from getResourceRouteFromValue
    const isCatalog = payload?.type === CloudItemType.Catalog
    return {
      link: {
        name: isCatalog ? RouteName.CloudCatalog : RouteName.CloudFile,
        params: isCatalog
          ? {
            folderId: payload.folderId,
            catalogCloudItemId: resource.resourceId,
          }
          : {fileCloudItemId: resource.resourceId},
      },
      iconClass: isCatalog ? IconNameType.Folder : IconNameType.File,
    }
  }],
  [ResourceType.App, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.AppApplication,
        params: {
          appId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Bolt,
    }
  }],
  [ResourceType.Cron, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.Crons,
        params: {},
      },
      iconClass: IconNameType.Clock,
    }
  }],
  [ResourceType.WebhookListener, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.WebhookListeners,
        params: {},
      },
      iconClass: IconNameType.Link,
    }
  }],
  [ResourceType.Connection, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.Connection,
        params: {
          connectionId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Link,
    }
  }],
  [ResourceType.Document, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.Document,
        params: {
          documentId: resource.resourceId,
        },
      },
      iconClass: IconNameType.FileLines,
    }
  }],
  [ResourceType.Whiteboard, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.Whiteboard,
        params: {
          whiteboardId: resource.resourceId,
        },
      },
      iconClass: IconNameType.PenRuler,
    }
  }],
  [ResourceType.Skill, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.Skill,
        params: {
          skillId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Lightbulb,
    }
  }],
  [ResourceType.Agent, (resource: IResponseResource) => {
    return {
      link: {
        name: RouteName.Agent,
        params: {
          agentId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Robot,
    }
  }],
  [ResourceType.DatabaseProperty, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IDatabasePropertyResponseInstance
    return {
      link: {
        name: RouteName.DatabasePropertySettings,
        params: {
          databaseId: payload.databaseId,
          propertyId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Table,
    }
  }],
  /**
   * Cell attachments have no resource items, so this entry exists for the registry's completeness
   * guarantee and for callers holding a global-search payload — the payload must carry `databaseId`
   * and `databaseItemId`, which the route needs alongside the attachment's own id.
   */
  [ResourceType.DatabaseItemFile, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as {databaseId?: string, databaseItemId?: string}
    return {
      link: {
        name: RouteName.DatabaseItemFile,
        params: {
          databaseId: payload?.databaseId,
          databaseItemId: payload?.databaseItemId,
          databaseItemFileId: resource.resourceId,
        },
      },
      iconClass: IconNameType.File,
    }
  }],
  [ResourceType.DatabaseSlice, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IDatabaseSliceResponseInstance
    return {
      link: {
        name: RouteName.DatabaseSliceSettings,
        params: {
          databaseId: payload.databaseId,
          databaseSliceId: resource.resourceId,
        },
      },
      iconClass: IconNameType.PizzaSlice,
    }
  }],
  [ResourceType.ConnectionDefinition, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IConnectionDefinitionResponseInstance
    return {
      link: {
        name: RouteName.AppApplication,
        params: {
          appId: payload.appId,
        },
      },
      iconClass: IconNameType.LinkSimple,
    }
  }],
  [ResourceType.DynamicFieldDefinition, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IDynamicFieldDefinitionResponseInstance
    return {
      link: {
        name: RouteName.AppDynamicFields,
        params: {
          appId: payload.automationAppId,
        },
      },
      iconClass: IconNameType.ListCheck,
    }
  }],
  [ResourceType.WebhookListenerDefinition, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IWebhookListenerDefinitionResponseInstance
    return {
      link: {
        name: RouteName.AppWebhooks,
        params: {
          appId: payload.automationAppId,
        },
      },
      iconClass: IconNameType.Webhook,
    }
  }],
  [ResourceType.AppAutomation, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IAutomationAppItemResponseInstance
    return {
      link: {
        name: RouteName.AppApplication,
        params: {
          appId: payload.automationAppId,
        },
      },
      iconClass: IconNameType.Bolt,
    }
  }],
  [ResourceType.AutomationRun, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IAutomationRunResponseInstance
    if(!payload?.automationId) {
      return {
        link: {
          name: RouteName.WorkspaceHome,
          params: {},
        },
        iconClass: IconNameType.Bolt,
      }
    }
    return {
      link: {
        name: RouteName.AutomationRunDetails,
        params: {
          automationId: payload.automationId,
          automationRunId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Bolt,
    }
  }],
  [ResourceType.AutomationVersion, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IAutomationVersionResponseInstance
    return {
      link: {
        name: RouteName.Automation,
        params: {
          automationId: payload.automationId,
        },
      },
      iconClass: IconNameType.Bolt,
    }
  }],
  [ResourceType.DatabaseItem, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IDatabaseItemResponseInstance
    return {
      link: {
        name: RouteName.DatabaseItem,
        params: {
          databaseId: payload.databaseId,
          databaseItemId: resource.resourceId,
        },
      },
      iconClass: IconNameType.Database,
    }
  }],
  [ResourceType.DashboardItem, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IDashboardItemResponseInstance
    return {
      link: {
        name: RouteName.Dashboard,
        params: {
          dashboardId: payload.dashboardId,
        },
      },
      iconClass: IconNameType.Grid2,
    }
  }],
  [ResourceType.ResourceGroup, () => {
    return {
      link: {
        name: RouteName.WorkspaceHome,
        params: {},
      },
      iconClass: IconNameType.LayerGroup,
    }
  }],
  [ResourceType.ResourceSpace, () => {
    return {
      link: {
        name: RouteName.WorkspaceHome,
        params: {},
      },
      iconClass: IconNameType.LayerGroup,
    }
  }],
  [ResourceType.ResourceItem, (resource: IResponseResource) => {
    const payload = resource.payload as unknown as IResourceItemResponseInstance
    const nested = payload?.resourceEntity
    const nestedDataFn = nested ? ResourceTypeDataMap.get(nested.resourceType) : undefined
    if(nested && nestedDataFn) {
      return nestedDataFn(nested)
    }
    return {
      link: {
        name: RouteName.WorkspaceHome,
        params: {},
      },
      iconClass: IconNameType.Box,
    }
  }],
])
